/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get, post } from '../../../services/GenesisApi';
import {
  ACTIVITY_TYPE_API,
  SERVICE_TYPE_API,
  ACTIVITY_CLASS_TYPE_API,
  POINT_TYPE_API,
  NATIONAL_BALANCING_POINT_API,
} from '../../../constants/Maintenance';
import { IActivityTypeItem } from '../../../types/Activity.type';

interface IData {
  data: [];
  isLoading: boolean;
}

interface IServiceTypeData {
  data: IServiceTypeItem[];
  isLoading: boolean;
}

interface IActivityClassTypeData {
  data: IActivityClassTypeItem[];
  isLoading: boolean;
}

interface INationalBalancingPointData {
  data: INationalBalancingPointItem[];
  isLoading: boolean;
}

export interface IPointItem {
  id: number;
  pointName: string;
  logicalMeter: string;
  isActive: boolean;
}

export interface INationalBalancingPointItem {
  id: number;
  nationalBalancingPointTypeName: string;
  meterId: string;
  isActive: boolean;
}

export interface IActivityTypeData {
  data: IActivityTypeItem[];
  totalRecords?: number;
  isLoading: boolean;
}

export interface IPointTypeData {
  data: IPointTypeItem[];
  isLoading: boolean;
}

export interface IServiceTypeItem {
  id: number;
  serviceId: number;
  serviceTypeName: string;
  isActive: number;
}

export interface IActivityClassTypeItem {
  id: number;
  status: string;
  activityClassTypeName: string;
  isActive: boolean;
}

export interface IPointTypeItem {
  id: number;
  status?: string;
  pointTypeName: string;
  isActive: boolean;
}

const initialData: IData = {
  data: [],
  isLoading: false,
};

const initialValue: IActivityTypeData = {
  data: [],
  totalRecords: 0,
  isLoading: false,
};

export interface IActivityMaintaintence {
  activityType: IActivityTypeData;
  serviceType: IServiceTypeData;
  activityClassType: IActivityClassTypeData;
  pointType: IPointTypeData;
  point: IPointItem[];
  nationalBalancingPoint: INationalBalancingPointData;
  logicalMeter: string;
  activityTypeDataAdvSearch: IActivityTypeData;
  // isLoading: boolean;
}

export const initialState: IActivityMaintaintence = {
  // isLoading: false,
  activityType: initialData,
  activityTypeDataAdvSearch: initialValue,
  serviceType: initialData,
  activityClassType: initialData,
  pointType: initialData,
  point: [],
  nationalBalancingPoint: initialData,
  logicalMeter: '',
};

export const getActivityType = createAsyncThunk(
  'maintenance/getActivityType',
  async () => {
    return get(ACTIVITY_TYPE_API, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getServiceType = createAsyncThunk(
  'maintenance/getServiceType',
  async () => {
    return get(SERVICE_TYPE_API, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getActivityClass = createAsyncThunk(
  'maintenance/getActivityClass',
  async (serviceId: number) => {
    return get(`${ACTIVITY_CLASS_TYPE_API}/${serviceId}`, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getPointType = createAsyncThunk(
  'maintenance/getPointType',
  async (serviceId: number) => {
    return get(`${POINT_TYPE_API}/${serviceId}`, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getNationalBalancingPoint = createAsyncThunk(
  'maintenance/getNationalBalancingPoint',
  async () => {
    return get(NATIONAL_BALANCING_POINT_API, {})
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

export const getActivityTypeDataAdvSearch = createAsyncThunk(
  'maintenance/getActivityTypeDataAdvSearch',
  async (requestBody: any) => {
    return post(`${ACTIVITY_TYPE_API}/search`, requestBody)
      .then((response) => {
        return response.data || [];
      })
      .catch((error) => {
        throw error;
      });
  }
);

const ActivityMaintaintence = createSlice({
  name: 'activityMaintaintence',
  initialState,
  reducers: {
    setValueForPoint: (
      state: IActivityMaintaintence,
      { payload }: PayloadAction<IPointItem[]>
    ) => {
      state.point = payload;
    },
    setLogicalMeter: (
      state: IActivityMaintaintence,
      { payload }: PayloadAction<string>
    ) => {
      state.logicalMeter = payload;
    },
    clearServiceTypeBasedFields: (state: IActivityMaintaintence) => {
      state.activityClassType.data = [];
      state.pointType.data = [];
      state.point = [];
    },
    clearActivityClassTypeBaseFields: (state: IActivityMaintaintence) => {
      state.pointType.data = [];
      state.point = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivityType.pending, (state: IActivityMaintaintence) => {
        state.activityType.isLoading = true;
      })
      .addCase(
        getActivityType.fulfilled,
        (
          state: IActivityMaintaintence,
          { payload }: PayloadAction<IActivityTypeItem[]>
        ) => {
          state.activityType.data = payload;
          state.activityType.isLoading = false;
        }
      )
      .addCase(getActivityType.rejected, (state: IActivityMaintaintence) => {
        state.activityType.isLoading = false;
      })
      .addCase(getServiceType.pending, (state: IActivityMaintaintence) => {
        state.serviceType.isLoading = true;
      })
      .addCase(
        getServiceType.fulfilled,
        (
          state: IActivityMaintaintence,
          { payload }: PayloadAction<IServiceTypeItem[]>
        ) => {
          state.serviceType.data = payload;
          state.serviceType.isLoading = false;
        }
      )
      .addCase(getServiceType.rejected, (state: IActivityMaintaintence) => {
        state.serviceType.isLoading = false;
      })
      .addCase(getActivityClass.pending, (state: IActivityMaintaintence) => {
        state.activityClassType.isLoading = true;
      })
      .addCase(
        getActivityClass.fulfilled,
        (
          state: IActivityMaintaintence,
          { payload }: PayloadAction<IActivityClassTypeItem[]>
        ) => {
          state.activityClassType.data = payload;
          state.activityClassType.isLoading = false;
        }
      )
      .addCase(getActivityClass.rejected, (state: IActivityMaintaintence) => {
        state.activityClassType.isLoading = false;
      })
      .addCase(getPointType.pending, (state: IActivityMaintaintence) => {
        state.pointType.isLoading = true;
      })
      .addCase(
        getPointType.fulfilled,
        (
          state: IActivityMaintaintence,
          { payload }: PayloadAction<IPointTypeItem[]>
        ) => {
          state.pointType.data = payload;
          state.pointType.isLoading = false;
        }
      )
      .addCase(getPointType.rejected, (state: IActivityMaintaintence) => {
        state.pointType.isLoading = false;
      })
      .addCase(
        getNationalBalancingPoint.pending,
        (state: IActivityMaintaintence) => {
          state.nationalBalancingPoint.isLoading = true;
        }
      )
      .addCase(
        getNationalBalancingPoint.fulfilled,
        (
          state: IActivityMaintaintence,
          { payload }: PayloadAction<INationalBalancingPointItem[]>
        ) => {
          state.nationalBalancingPoint.data = payload;
          state.nationalBalancingPoint.isLoading = false;
        }
      )
      .addCase(
        getNationalBalancingPoint.rejected,
        (state: IActivityMaintaintence) => {
          state.nationalBalancingPoint.isLoading = false;
        }
      )
      .addCase(
        getActivityTypeDataAdvSearch.pending,
        (state: IActivityMaintaintence) => {
          state.activityTypeDataAdvSearch.isLoading = true;
        }
      )
      .addCase(
        getActivityTypeDataAdvSearch.fulfilled,
        (state: IActivityMaintaintence, { payload }: PayloadAction<any>) => {
          state.activityTypeDataAdvSearch.data = payload?.activitys;
          state.activityTypeDataAdvSearch.totalRecords = payload?.count;
          state.activityTypeDataAdvSearch.isLoading = false;
        }
      )
      .addCase(
        getActivityTypeDataAdvSearch.rejected,
        (state: IActivityMaintaintence) => {
          state.activityTypeDataAdvSearch.isLoading = false;
        }
      );
  },
});

export const activityActions = ActivityMaintaintence.actions;
export default ActivityMaintaintence.reducer;
